import { useState, useRef, useEffect } from "react";
import { Share2, Copy, Link as LinkIcon, Mail, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Share dropdown for blog posts.
 * Includes: copy link, copy Instagram-friendly snippet, LinkedIn, Twitter/X, Facebook,
 * WhatsApp, Email, and native Web Share when available (mobile).
 */
const BlogShareButton = ({ title, url, excerpt, slug }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedIg, setCopiedIg] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || "The Creative Ensemble";
  const shareExcerpt = excerpt || "";

  // Social previews: point crawlers at the backend's /api/share/blog/{slug}
  // endpoint so LinkedIn/Twitter/Facebook/WhatsApp/Slack see proper OG tags
  // (the page auto-redirects real browsers to the SPA route).
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const previewUrl = slug ? `${apiBase}/api/share/blog/${slug}` : shareUrl;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy link"); }
  };

  const copyForInstagram = async () => {
    const text = `${shareTitle}${shareExcerpt ? `\n\n${shareExcerpt}` : ""}\n\nRead more (link in bio):\n${shareUrl}\n\nvia @thecreativeensemble`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIg(true);
      toast.success("Instagram-friendly caption copied. Paste it into your post or story.");
      setTimeout(() => setCopiedIg(false), 1800);
    } catch { toast.error("Could not copy caption"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareExcerpt, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(previewUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(previewUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${previewUrl}`)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareExcerpt ? shareExcerpt + "\n\n" : ""}${previewUrl}`)}`;

  const Item = ({ onClick, href, icon: Icon, label, testId, color = "#0A0A0A" }) => {
    const cls = "w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#FFC2E8] transition-colors text-left";
    if (href) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId} onClick={() => setOpen(false)}>
          <Icon className="w-4 h-4" style={{ color }} /> {label}
        </a>
      );
    }
    return (
      <button type="button" onClick={onClick} className={cls} data-testid={testId}>
        <Icon className="w-4 h-4" style={{ color }} /> {label}
      </button>
    );
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => {
          // On touch devices, prefer native share sheet
          if (typeof navigator !== "undefined" && navigator.share && /Mobi|Android/i.test(navigator.userAgent || "")) {
            nativeShare();
          } else {
            setOpen((v) => !v);
          }
        }}
        className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-bold rounded-full px-4 py-2 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest"
        data-testid="blog-share-trigger"
      >
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white border-2 border-[#0A0A0A] shadow-xl z-50 py-1"
          data-testid="blog-share-menu"
        >
          <Item
            onClick={copyLink}
            icon={copied ? Check : LinkIcon}
            label={copied ? "Copied!" : "Copy link"}
            testId="share-copy-link"
            color="#FF2D87"
          />
          <Item
            onClick={copyForInstagram}
            icon={copiedIg ? Check : Copy}
            label={copiedIg ? "Caption copied!" : "Copy for Instagram"}
            testId="share-copy-instagram"
            color="#FF2D87"
          />
          <div className="border-t border-[#E5E5E5] my-1" />
          <Item href={linkedinUrl} icon={LinkIcon} label="Share on LinkedIn" testId="share-linkedin" color="#0077B5" />
          <Item href={twitterUrl} icon={LinkIcon} label="Share on X / Twitter" testId="share-twitter" color="#0A0A0A" />
          <Item href={facebookUrl} icon={LinkIcon} label="Share on Facebook" testId="share-facebook" color="#1877F2" />
          <Item href={whatsappUrl} icon={LinkIcon} label="Share on WhatsApp" testId="share-whatsapp" color="#25D366" />
          <Item href={mailUrl} icon={Mail} label="Email this" testId="share-email" color="#5170FF" />
        </div>
      )}
    </div>
  );
};

export default BlogShareButton;
